/**
 * Aviators Market — Search Results Page JS
 * Handles: AJAX filtering (no page reload), view toggle, sort/per-page,
 *          filter drawer (mobile), load more, active filter chips,
 *          lead modal, URL state / popstate.
 */
(function () {
  'use strict';

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return Array.from((ctx || document).querySelectorAll(sel)); }

  /* ─── AVIATOR-250: deferred listing cards ───
     Shopify never exposes query params to Liquid on this store, so the server
     can't render "page 2" on demand. Instead it renders every result up-front and
     wraps everything past the first page in a hidden .am-deferred-card. These
     helpers reveal them and count only what's actually on screen. */

  function visibleCardCount(grid) {
    return qsa('.am-listing-card', grid).filter(function (card) {
      return !card.closest('.am-deferred-card');
    }).length;
  }

  /** Unwrap up to `howMany` deferred cards in place. Returns the revealed cards. */
  function revealDeferredCards(grid, howMany) {
    var revealed = [];
    if (!grid || howMany < 1) return revealed;

    qsa('.am-deferred-card', grid).slice(0, howMany).forEach(function (wrap) {
      while (wrap.firstChild) {
        var node = wrap.firstChild;
        wrap.parentNode.insertBefore(node, wrap);
        if (node.nodeType === 1 && node.classList.contains('am-listing-card')) {
          revealed.push(node);
        }
      }
      wrap.parentNode.removeChild(wrap);
    });

    return revealed;
  }

  function clearLoadMoreParams(url) {
    url.searchParams.delete('am_partial');
    url.searchParams.delete('am_offset');
    url.searchParams.delete('am_limit');
  }

  function buildUrl(params) {
    var url = new URL(window.location.href);
    Object.keys(params).forEach(function (key) {
      if (params[key] === null || params[key] === '') {
        url.searchParams.delete(key);
      } else {
        url.searchParams.set(key, params[key]);
      }
    });
    if (params.page === null) {
      clearLoadMoreParams(url);
    }
    var sorted = new URLSearchParams(
      Array.from(url.searchParams.entries()).sort(function (a, b) {
        return a[0].localeCompare(b[0]);
      })
    );
    url.search = sorted.toString();
    return url.toString();
  }

  function slugifyLocation(val) {
    return val.trim().toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
  }

  function resolveLocationTag(raw) {
    if (!raw || !raw.trim()) return null;
    var v = slugifyLocation(raw);
    if (v === 'us' || v === 'united-states' || v === 'unitedstates') return 'location-us';
    // Must match the portal's tags (ShopifyProductService::locationTag).
    if (v === 'canada' || v === 'ca') return 'location-ca';
    if (v === 'international') return 'location-international';
    if (v.indexOf('location-') === 0) return v;
    return 'location-' + v;
  }

  // Old links and the home-page search send ?location=canada (or the retired
  // location-canada tag); make the URL carry exactly one correct location tag
  // so the results, the sidebar and the chip agree. Returns true if redirecting.
  function normalizeLocationUrl() {
    var url = new URL(window.location.href);
    var loc = url.searchParams.get('location');
    var want = loc ? resolveLocationTag(loc) : null;
    var legacy = { 'location-canada': 'location-ca' };
    var tags = url.searchParams.getAll('filter.p.tag');
    var next = [];
    var seen = {};
    var changed = false;
    tags.forEach(function (t) {
      var n = legacy[t] || t;
      if (n !== t) changed = true;
      if (want && n.indexOf('location-') === 0 && n !== want) { changed = true; return; }
      if (seen[n]) { changed = true; return; }
      seen[n] = true;
      next.push(n);
    });
    if (want && !seen[want]) { next.push(want); changed = true; }
    if (!changed) return false;
    url.searchParams.delete('filter.p.tag');
    next.forEach(function (t) { url.searchParams.append('filter.p.tag', t); });
    url.searchParams.delete('page');
    window.location.replace(url.toString());
    return true;
  }

  function appendLocationFilter(url, locationRaw) {
    if (!locationRaw || !locationRaw.trim()) return;
    var slug = slugifyLocation(locationRaw);
    url.searchParams.set('location', slug);
    var tag = resolveLocationTag(locationRaw);
    if (tag) url.searchParams.append('filter.p.tag', tag);
  }

  function parsePriceInput(val) {
    if (val === '' || val == null) return null;
    var n = parseFloat(String(val).replace(/,/g, ''));
    return isNaN(n) ? null : n;
  }

  function updateKeywordBreadcrumb(targetUrl) {
    var url = new URL(targetUrl || window.location.href, window.location.origin);
    var q = url.searchParams.get('q');
    var crumbs = qs('.am-search-crumbs');
    if (!crumbs) return;

    var qTrim = q ? String(q).trim() : '';

    if (qTrim) {
      var existingKw = crumbs.querySelector('[data-am-crumb-keyword]');
      if (existingKw) {
        var existingQ = existingKw.textContent.replace(/^"|"$/g, '').trim();
        if (existingQ.toLowerCase() === qTrim.toLowerCase()) return;
      }
    }

    crumbs.querySelectorAll('[data-am-crumb-keyword]').forEach(function (el) {
      var prev = el.previousElementSibling;
      if (prev && prev.classList.contains('am-crumb-sep')) prev.remove();
      el.remove();
    });

    crumbs.querySelectorAll('strong').forEach(function (strong) {
      if (strong.textContent.charAt(0) === '"') {
        var sepBefore = strong.previousElementSibling;
        if (sepBefore && sepBefore.classList.contains('am-crumb-sep')) sepBefore.remove();
        strong.remove();
      }
    });

    while (crumbs.lastElementChild && crumbs.lastElementChild.classList.contains('am-crumb-sep')) {
      crumbs.lastElementChild.remove();
    }

    if (!qTrim) return;

    var sep = document.createElement('span');
    sep.className = 'am-crumb-sep';
    sep.setAttribute('aria-hidden', 'true');
    sep.textContent = '/';

    var kw = document.createElement('strong');
    kw.setAttribute('data-am-crumb-keyword', '');
    kw.textContent = '"' + qTrim + '"';

    crumbs.appendChild(sep);
    crumbs.appendChild(kw);
  }

  function syncHeroSearchFromUrl(targetUrl) {
    var url = new URL(targetUrl, window.location.origin);
    var q = url.searchParams.get('q');
    var loc = url.searchParams.get('location');
    var keywordInput = qs('.am-search-bar input[name="q"]');
    var locSel = qs('.am-search-bar select[name="location"]');
    var clearBtn = qs('.am-search-clear-btn');

    if (keywordInput) {
      keywordInput.value = q || '';
      if (clearBtn) clearBtn.hidden = !keywordInput.value.trim();
    }
    if (locSel) locSel.value = loc || '';
  }

  /* ─── AJAX Fetch & Swap ─── */

  var isFetching = false;
  var queuedUrl = null;
  var autoApplyPending = false; // a filter change is waiting to be submitted

  function getResultsSectionId() {
    var el = qs('#AmSearchResults');
    if (el && el.dataset.sectionId) return el.dataset.sectionId;
    var wrap = el ? el.closest('[id^="shopify-section-"]') : null;
    if (wrap && wrap.id) return wrap.id.replace('shopify-section-', '');
    return null;
  }

  function getSectionHtmlFromResponse(data, sectionId) {
    if (!data || !data.sections) return null;
    if (sectionId && data.sections[sectionId]) return data.sections[sectionId];
    var keys = Object.keys(data.sections);
    return keys.length ? data.sections[keys[0]] : null;
  }

  function buildSectionFetchUrl(targetUrl) {
    var sectionId = getResultsSectionId();
    var url = new URL(targetUrl, window.location.origin);
    url.searchParams.delete('sections');
    url.searchParams.delete('_amcb');
    url.searchParams.delete('per_page');
    if (sectionId) url.searchParams.set('sections', sectionId);
    url.searchParams.set('_amcb', String(Date.now()));
    return url.toString();
  }

  function applySwappedSection(doc, targetUrl) {
    var newResults = doc.querySelector('#AmResultsArea');
    var currentResults = qs('#AmResultsArea');
    if (newResults && currentResults) {
      currentResults.innerHTML = newResults.innerHTML;
    }

    var newPanel = doc.querySelector('#AmFilterPanel .am-filter-body');
    var currentPanel = qs('#AmFilterPanel .am-filter-body');
    if (newPanel && currentPanel) {
      currentPanel.innerHTML = newPanel.innerHTML;
    }

    syncHeroSearchFromUrl(targetUrl);
    updateKeywordBreadcrumb(targetUrl);
    if (window.location.href === targetUrl) {
      history.replaceState(null, '', targetUrl);
    } else {
      history.pushState(null, '', targetUrl);
    }
    reinitAfterSwap();
    updateShowingRange();
  }

  function getTotalListings() {
    var panel = qs('#AmLoadPanel');
    if (panel && panel.dataset.total) {
      var t = parseInt(panel.dataset.total, 10);
      if (!isNaN(t) && t > 0) return t;
    }
    var countWrap = qs('#AmResultCount strong');
    if (countWrap) {
      var m = countWrap.textContent.match(/(\d+)/);
      if (m) return parseInt(m[1], 10);
    }
    return 0;
  }

  function syncPerPageSelect(value) {
    var sel = qs('#AmPerPage');
    if (sel && value != null) sel.value = String(value);
  }

  function trimGridToCount(grid, targetSize) {
    var cardCount = 0;
    Array.from(grid.children).forEach(function (node) {
      // AVIATOR-250 — never discard the deferred reserve; those cards are the
      // rest of the results and "Load More" still needs them.
      if (node.classList.contains('am-deferred-card')) return;

      if (node.classList.contains('am-listing-card')) {
        cardCount += 1;
        // Re-hide rather than destroy, so the card can be revealed again.
        if (cardCount > targetSize) {
          var wrap = document.createElement('div');
          wrap.className = 'am-deferred-card';
          wrap.hidden = true;
          node.parentNode.insertBefore(wrap, node);
          wrap.appendChild(node);
        }
      } else if (cardCount >= targetSize) {
        node.remove();
      }
    });
  }

  function fetchPartialCards(offset, limit) {
    var url = new URL(window.location.href);
    clearLoadMoreParams(url);
    url.searchParams.set('am_partial', 'load_more');
    url.searchParams.set('am_offset', String(offset));
    url.searchParams.set('am_limit', String(limit));
    var page = Math.floor(offset / AM_LOAD_MORE_CHUNK) + 1;
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }

    return fetch(url.toString(), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('partial fetch failed');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var partial = doc.querySelector('#AmLoadMorePartial');
        var source = partial || doc.querySelector('#AmListingGrid');
        var cards = [];
        if (source) {
          qsa('.am-listing-card, .am-ingrid-ad', source).forEach(function (el) {
            cards.push(el);
          });
        }
        return cards;
      });
  }

  function ensurePageSize(targetSize) {
    var grid = qs('#AmListingGrid');
    var panel = qs('#AmLoadPanel');
    if (!grid || isNaN(targetSize) || targetSize < 1) return Promise.resolve();

    var total = getTotalListings();
    var target = total > 0 ? Math.min(targetSize, total) : targetSize;
    var current = visibleCardCount(grid);

    /* AVIATOR-250 — if the server pre-rendered the rest of the results as hidden
       .am-deferred-card wrappers, satisfy a larger page size by revealing those
       instead of fetching (the fetch can't page on this store). */
    if (current < target) {
      var shown = revealDeferredCards(grid, target - current);
      if (shown.length) {
        // Keep revealed cards in the active grid/list view.
        var gview = grid.dataset.view || localStorage.getItem('am_view') || 'grid';
        shown.forEach(function (card) {
          card.classList.remove('am-listing-card--grid', 'am-listing-card--list');
          card.classList.add('am-listing-card--' + gview);
        });
        reinitWishlistOnAppendedCards(shown);
      }
      current = visibleCardCount(grid);

      // Revealing satisfied the request — refresh the counters and stop.
      if (current >= target || !qsa('.am-deferred-card', grid).length) {
        if (panel) {
          panel.dataset.nextOffset = String(current);
          panel.style.display = qsa('.am-deferred-card', grid).length ? '' : 'none';
        }
        updateShowingRange();
        syncPerPageSelect(targetSize);
        return Promise.resolve();
      }
    }

    if (current > target) {
      trimGridToCount(grid, target);
      if (panel) {
        panel.dataset.nextOffset = String(target);
        panel.style.display = target < total ? '' : 'none';
      }
      updateShowingRange();
      syncPerPageSelect(targetSize);
      return Promise.resolve();
    }

    if (current >= target) {
      syncPerPageSelect(targetSize);
      return Promise.resolve();
    }

    var needed = target - current;
    return fetchPartialCards(current, needed).then(function (newNodes) {
      var appendedCards = [];
      newNodes.forEach(function (node) {
        grid.appendChild(node);
        if (node.classList.contains('am-listing-card')) appendedCards.push(node);
      });

      reinitWishlistOnAppendedCards(appendedCards);

      var view = grid.dataset.view || localStorage.getItem('am_view') || 'grid';
      qsa('.am-listing-card', grid).forEach(function (card) {
        card.classList.remove('am-listing-card--grid', 'am-listing-card--list');
        card.classList.add('am-listing-card--' + view);
      });

      var newCount = qsa('.am-listing-card', grid).length;
      if (newCount > target) {
        trimGridToCount(grid, target);
        newCount = target;
      }
      if (panel) {
        panel.dataset.nextOffset = String(newCount);
        panel.style.display = newCount < total ? '' : 'none';
      }
      updateShowingRange();
      syncPerPageSelect(targetSize);

      if (newCount < target && appendedCards.length > 0) {
        return ensurePageSize(targetSize);
      }
    });
  }

  function reconcilePageSizeFromUrl() {
    if (!qs('#AmPerPage') || isFetching) return;

    var url = new URL(window.location.href);
    var sel = qs('#AmPerPage');
    var wanted = url.searchParams.get('page_size') || url.searchParams.get('per_page') || (sel ? sel.value : null);
    if (!wanted) return;

    if (url.searchParams.get('per_page') && !url.searchParams.get('page_size')) {
      url.searchParams.set('page_size', url.searchParams.get('per_page'));
      url.searchParams.delete('per_page');
      history.replaceState(null, '', url.toString());
    } else if (!url.searchParams.get('page_size')) {
      url.searchParams.set('page_size', wanted);
      history.replaceState(null, '', url.toString());
    }

    var wantedNum = parseInt(wanted, 10);
    if (isNaN(wantedNum)) return;

    // AVIATOR-250 — hidden deferred cards must not count as already-shown.
    var cards = visibleCardCount(qs('#AmListingGrid') || document);
    var total = getTotalListings();
    var expected = Math.min(wantedNum, total || wantedNum);

    syncPerPageSelect(wanted);
    if (cards !== expected) {
      ensurePageSize(expected);
    }
  }

  function parseFetchedDocument(response, sectionId) {
    var ct = response.headers.get('content-type') || '';
    if (ct.indexOf('application/json') === -1) {
      return response.text().then(function (html) {
        return new DOMParser().parseFromString(html, 'text/html');
      });
    }
    return response.json().then(function (data) {
      var html = getSectionHtmlFromResponse(data, sectionId);
      if (html) return new DOMParser().parseFromString(html, 'text/html');
      return new DOMParser().parseFromString('', 'text/html');
    });
  }

  function fetchFullPageDocument(targetUrl) {
    return fetch(targetUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('fetch failed');
        return r.text();
      })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        if (!doc.querySelector('#AmResultsArea')) throw new Error('results missing');
        return doc;
      });
  }

  function fetchAndSwap(targetUrl) {
    // A newer request while one is loading (e.g. ticking a second filter) is
    // remembered and run when the current one finishes; the older response is
    // then discarded so it can't overwrite the newer selection.
    if (isFetching) {
      queuedUrl = targetUrl;
      return Promise.resolve();
    }
    isFetching = true;

    var sectionId = getResultsSectionId();
    var fetchUrl = sectionId ? buildSectionFetchUrl(targetUrl) : targetUrl;

    var resultsArea = qs('#AmResultsArea');
    if (resultsArea) resultsArea.classList.add('am-results-area--loading');

    return fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        if (!r.ok) throw new Error('fetch failed');
        return parseFetchedDocument(r, sectionId);
      })
      .then(function (doc) {
        if (!doc.querySelector('#AmResultsArea')) throw new Error('results missing');
        return doc;
      })
      .catch(function () {
        return fetchFullPageDocument(targetUrl);
      })
      .then(function (doc) {
        // Superseded by a newer selection (sent or about to be sent): keep the
        // user's sidebar as is instead of redrawing it with older results.
        if (queuedUrl || autoApplyPending) return;
        applySwappedSection(doc, targetUrl);
      })
      .finally(function () {
        isFetching = false;
        if (queuedUrl) {
          var next = queuedUrl;
          queuedUrl = null;
          fetchAndSwap(next);
          return;
        }
        if (autoApplyPending) return; // stays loading until that change is sent
        var area = qs('#AmResultsArea');
        if (area) area.classList.remove('am-results-area--loading');
      });
  }

  function applyMostViewedSort() {
    var url = new URL(window.location.href);
    // Triggered by am_sort=most-viewed (Shopify sorts by a real value; we reorder here).
    if (url.searchParams.get('am_sort') !== 'most-viewed' && url.searchParams.get('sort_by') !== 'most-viewed') return;

    var grid = qs('#AmListingGrid');
    if (!grid) return;

    var nodes = Array.from(grid.children);
    nodes.sort(function (a, b) {
      var av = parseInt(a.dataset.viewCount || '0', 10);
      var bv = parseInt(b.dataset.viewCount || '0', 10);
      if (bv !== av) return bv - av;
      var ah = a.dataset.productHandle || '';
      var bh = b.dataset.productHandle || '';
      return ah.localeCompare(bh);
    });
    nodes.forEach(function (node) { grid.appendChild(node); });
  }

  function reinitAfterSwap() {
    initMarketTabs();
    initViewToggle();
    initLoadMore();
    initFilterApply();
    initFilterAutoApply();
    initToolbarControls();
    initFilterDrawer();
    applyMostViewedSort();

    if (typeof window.amApplyMarketFilters === 'function') {
      window.amApplyMarketFilters();
    }
    if (typeof window.amInitFilterInteractions === 'function') {
      window.amInitFilterInteractions();
    }
    if (typeof window.amUpdateLiveCounts === 'function') {
      window.amUpdateLiveCounts();
    }

    var allCards = qsa('.am-listing-card');
    reinitWishlistOnAppendedCards(allCards);

    var resultsArea = qs('#AmResultsArea');
    if (resultsArea) {
      resultsArea.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  /* ─── Navigate (AJAX) ─── */

  function navigate(params) {
    var target = buildUrl(params);
    fetchAndSwap(target).catch(function () {
      // AJAX swap failed (rate-limit / cache / parse) — never eat the user's
      // action: fall back to a real navigation so the result always applies.
      window.location.assign(target);
    });
  }

  /* ─── View Toggle ─── */

  function initViewToggle() {
    var grid = qs('#AmListingGrid');
    var btns = qsa('.am-view-btn');
    if (!grid || !btns.length) return;

    var params = new URLSearchParams(window.location.search);
    var urlView = params.get('view');
    var stored = (urlView === 'grid' || urlView === 'list') ? urlView : (localStorage.getItem('am_view') || 'grid');
    applyView(stored);
    if (urlView === 'grid' || urlView === 'list') {
      localStorage.setItem('am_view', urlView);
    }

    btns.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var view = btn.dataset.view;
        applyView(view);
        localStorage.setItem('am_view', view);
        var url = new URL(window.location.href);
        url.searchParams.set('view', view);
        history.replaceState(null, '', url.toString());
      });
    });

    function applyView(view) {
      grid.className = grid.className.replace(/am-listing-grid--(grid|list)/g, '');
      grid.classList.add('am-listing-grid--' + view);
      grid.dataset.view = view;
      qsa('.am-listing-card', grid).forEach(function (card) {
        card.classList.remove('am-listing-card--grid', 'am-listing-card--list');
        card.classList.add('am-listing-card--' + view);
      });
      btns.forEach(function (b) {
        var active = b.dataset.view === view;
        b.classList.toggle('am-view-btn--active', active);
        b.setAttribute('aria-pressed', String(active));
      });
    }
  }

  /* ─── Toolbar Controls ─── */

  function initToolbarControls() {
    var sortSel = qs('#AmSortBy');
    var perPageSel = qs('#AmPerPage');

    if (sortSel) {
      // The rendered "selected" option can be stale (cached HTML). Sync the
      // dropdown to the URL — otherwise clicking the shown value is a dead
      // click (selects don't fire change on the same value).
      var u = new URL(window.location.href);
      var urlSort = u.searchParams.get('am_sort') === 'most-viewed'
        ? 'most-viewed'
        : (u.searchParams.get('sort_by') || (window.location.pathname.indexOf('/collections/') === 0 ? 'manual' : 'created-descending'));
      if (sortSel.value !== urlSort) {
        for (var i = 0; i < sortSel.options.length; i++) {
          if (sortSel.options[i].value === urlSort) { sortSel.value = urlSort; break; }
        }
      }
    }

    if (sortSel && !sortSel.dataset.amBound) {
      sortSel.dataset.amBound = '1';
      sortSel.addEventListener('change', function () {
        try { sessionStorage.setItem('am_sort_touched', '1'); } catch (e) {}
        var choice = sortSel.value;
        // Sorting uses a full page navigation: Shopify renders the sorted list
        // server-side and the URL stays the single source of truth. (AJAX here
        // failed silently under rate-limits, eating the user's selection.)
        var target;
        var bust = String(Date.now()); // defeat edge-cached HTML variants
        if (choice === 'most-viewed') {
          // "Most viewed" is a client-side reorder over a real Shopify sort.
          target = buildUrl({ sort_by: (window.location.pathname.indexOf('/collections/') === 0 ? null : 'created-descending'), am_sort: 'most-viewed', _ambust: bust, page: null, am_partial: null, am_offset: null, am_limit: null });
        } else {
          // "Newest first" on a collection is its stored order — the default —
          // so it uses the clean URL (no sort_by). robots.txt blocks sort_by URLs,
          // and the clean URL is what search engines index.
          var sortParam = (choice === 'manual') ? null : choice;
          target = buildUrl({ sort_by: sortParam, am_sort: null, _ambust: bust, page: null, am_partial: null, am_offset: null, am_limit: null });
        }
        window.location.assign(target);
      });
    }

    if (perPageSel && !perPageSel.dataset.amBound) {
      perPageSel.dataset.amBound = '1';
      perPageSel.addEventListener('change', function () {
        window.location.href = buildUrl({
          page_size: perPageSel.value,
          per_page: null,
          page: null,
          am_partial: null,
          am_offset: null,
          am_limit: null
        });
      });
    }
  }

  /* ─── XB Wishlist Reinit ─── */

  function reinitWishlistOnAppendedCards(cards) {
    if (!cards || !cards.length) return;

    try {
      var xb = window.XBWishlist || window.xbWishlist || window.XOWishlist || window.xoWishlist;
      if (xb && typeof xb.init === 'function') xb.init();
      else if (xb && typeof xb.refresh === 'function') xb.refresh();
      else if (xb && typeof xb.scan === 'function') xb.scan();
    } catch (e) { /* swallow */ }

    var events = ['xb:wishlist:refresh', 'xo:wishlist:refresh', 'wishlist:refresh', 'shopify:section:load'];
    events.forEach(function (name) {
      try { document.dispatchEvent(new CustomEvent(name, { bubbles: true, detail: { cards: cards } })); } catch (e) { /* swallow */ }
    });

    cards.forEach(function (card) {
      try { card.dispatchEvent(new CustomEvent('xb:wishlist:attach', { bubbles: true })); } catch (e) { /* swallow */ }
    });
  }

  /* ─── Load More ─── */

  var AM_LOAD_MORE_CHUNK = 250;

  function updateShowingRange() {
    var rangeEl = qs('[data-am-showing-range]');
    var panel = qs('#AmLoadPanel');
    var grid = qs('#AmListingGrid');
    if (!rangeEl || !grid) return;
    var total = panel ? parseInt(panel.dataset.total, 10) : 0;
    var shown = visibleCardCount(grid);
    if (!total) {
      var countWrap = qs('#AmResultCount strong');
      if (countWrap) {
        var m = countWrap.textContent.match(/(\d+)/);
        if (m) total = parseInt(m[1], 10);
      }
    }
    if (shown > 0 && total > 0) {
      rangeEl.textContent = 'Showing 1–' + Math.min(shown, total) + ' of ' + total;
    }
  }

  function buildLoadMoreUrl(panel) {
    var offset = parseInt(panel.dataset.nextOffset, 10) || 0;
    var batch = parseInt(panel.dataset.loadBatch, 10) || 50;
    var perPage = parseInt(panel.dataset.paginateBy, 10) || 48;
    var url = new URL(window.location.href);
    clearLoadMoreParams(url);
    url.searchParams.set('am_partial', 'load_more');
    url.searchParams.set('am_offset', String(offset));
    url.searchParams.set('am_limit', String(batch));

    /* AVIATOR-250 — page through using Shopify's own ?page= param.
       am_offset is ignored server-side (query params never reach Liquid on this
       store), so paging by the RENDERED page size is what actually advances the
       results. Previously this divided by 250 — the paginate size, not the page
       size — so `page` stayed at 1 and every "load more" refetched page 1. */
    var page = Math.floor(offset / perPage) + 1;
    if (page > 1) {
      url.searchParams.set('page', String(page));
    } else {
      url.searchParams.delete('page');
    }
    return url.toString();
  }

  function initLoadMore() {
    var btn = qs('#AmLoadMore');
    var grid = qs('#AmListingGrid');
    var panel = qs('#AmLoadPanel');
    if (!btn || !grid || !panel) return;
    if (btn.dataset.amBound === '1') return;
    btn.dataset.amBound = '1';

    btn.addEventListener('click', function () {
      var offset = parseInt(panel.dataset.nextOffset, 10) || 0;
      var total = parseInt(panel.dataset.total, 10) || 0;
      if (offset >= total) return;

      /* AVIATOR-250 — reveal the cards the server already rendered (hidden past
         paginate_by) instead of re-fetching. The AJAX path can't page on this
         store because Shopify withholds query params from Liquid, so it kept
         returning the first page and the grid repeated. */
      if (qsa('.am-deferred-card', grid).length) {
        var batch = parseInt(panel.dataset.loadBatch, 10) || 50;
        var revealed = revealDeferredCards(grid, batch);

        // Keep revealed cards in the current grid/list view.
        var view = grid.dataset.view || localStorage.getItem('am_view') || 'grid';
        revealed.forEach(function (card) {
          card.classList.remove('am-listing-card--grid', 'am-listing-card--list');
          card.classList.add('am-listing-card--' + view);
        });

        panel.dataset.nextOffset = String(visibleCardCount(grid));
        if (!qsa('.am-deferred-card', grid).length) {
          panel.style.display = 'none';
        }
        updateShowingRange();
        reinitWishlistOnAppendedCards(revealed);
        return;
      }

      var fetchUrl = buildLoadMoreUrl(panel);
      var textEl = btn.querySelector('.am-load-more-text');
      var spinEl = btn.querySelector('.am-load-more-spinner');
      btn.disabled = true;
      if (textEl) textEl.style.display = 'none';
      if (spinEl) spinEl.style.display = '';

      fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var partial = doc.querySelector('#AmLoadMorePartial');
          var source = partial || doc.querySelector('#AmListingGrid');
          var appendedCards = [];
          var listingCount = 0;

          /* AVIATOR-250 — de-duplicate what we append.
             Shopify does not expose query params to Liquid on this store
             (request.query_string is always empty), so the server can't honour
             am_offset: the "load more" response is the SAME first page again.
             Without this guard the grid re-appends listings 1..48 and the user
             sees the catalogue repeat after ~48 results. Track the product
             handles already on screen and only append ones we haven't shown. */
          var seen = Object.create(null);
          qsa('.am-listing-card', grid).forEach(function (card) {
            var a = card.querySelector('a[href*="/products/"]');
            if (!a) return;
            var m = a.getAttribute('href').match(/\/products\/([^/?#]+)/);
            if (m) seen[m[1]] = true;
          });

          if (source) {
            qsa('.am-listing-card, .am-ingrid-ad', source).forEach(function (card) {
              if (card.classList.contains('am-listing-card')) {
                var link = card.querySelector('a[href*="/products/"]');
                var mm = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
                var handle = mm ? mm[1] : null;
                if (!handle || seen[handle]) return; // already on screen
                seen[handle] = true;
                listingCount += 1;
              }
              grid.appendChild(card);
              appendedCards.push(card);
            });
          }

          var newOffset = offset + listingCount;
          panel.dataset.nextOffset = String(newOffset);

          if (newOffset >= total || listingCount === 0) {
            panel.style.display = 'none';
          } else {
            btn.disabled = false;
            if (textEl) textEl.style.display = '';
            if (spinEl) spinEl.style.display = 'none';
          }

          updateShowingRange();
          reinitWishlistOnAppendedCards(appendedCards);

          var view = grid.dataset.view || localStorage.getItem('am_view') || 'grid';
          qsa('.am-listing-card', grid).forEach(function (card) {
            card.classList.remove('am-listing-card--grid', 'am-listing-card--list');
            card.classList.add('am-listing-card--' + view);
          });
        })
        .catch(function () {
          btn.disabled = false;
          if (textEl) textEl.style.display = '';
          if (spinEl) spinEl.style.display = 'none';
        });
    });
  }

  /* ─── Filter Drawer (Mobile) ─── */

  function openFilterDrawer() {
    var panel = qs('#AmFilterPanel');
    var overlay = qs('#AmFilterOverlay');
    var openBtn = qs('#AmFilterOpen');
    if (!panel) return;
    panel.style.display = 'flex';
    panel.classList.add('am-filter-panel--open');
    if (overlay) overlay.classList.add('am-filter-overlay--visible');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
  }

  function closeFilterDrawer() {
    var panel = qs('#AmFilterPanel');
    var overlay = qs('#AmFilterOverlay');
    var openBtn = qs('#AmFilterOpen');
    if (panel) panel.classList.remove('am-filter-panel--open');
    if (overlay) overlay.classList.remove('am-filter-overlay--visible');
    if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setTimeout(function () {
      var p = qs('#AmFilterPanel');
      if (p && !p.classList.contains('am-filter-panel--open')) p.style.display = '';
    }, 320);
  }

  function initFilterDrawer() {
    var panel = qs('#AmFilterPanel');
    if (!panel) return;

    if (!qs('#AmFilterOverlay')) {
      var overlay = document.createElement('div');
      overlay.className = 'am-filter-overlay';
      overlay.id = 'AmFilterOverlay';
      document.body.appendChild(overlay);
      overlay.addEventListener('click', closeFilterDrawer);
    }

    if (!qs('#AmFilterOpen')) {
      var openBtn = document.createElement('button');
      openBtn.className = 'am-btn am-btn-outline am-filter-open-btn';
      openBtn.type = 'button';
      openBtn.id = 'AmFilterOpen';
      openBtn.setAttribute('aria-expanded', 'false');
      openBtn.setAttribute('aria-controls', 'AmFilterPanel');
      openBtn.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="4" y1="6" x2="20" y2="6"/><line x1="4" y1="12" x2="16" y2="12"/><line x1="4" y1="18" x2="12" y2="18"/></svg> Filters';
      openBtn.addEventListener('click', openFilterDrawer);
      var toolbar = qs('#AmToolbar');
      if (toolbar) toolbar.prepend(openBtn);
    }

    if (!qs('#AmFilterClose')) {
      var closeBtn = document.createElement('button');
      closeBtn.className = 'am-filter-close-btn';
      closeBtn.type = 'button';
      closeBtn.id = 'AmFilterClose';
      closeBtn.setAttribute('aria-label', 'Close filters');
      closeBtn.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" aria-hidden="true" focusable="false"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>';
      closeBtn.addEventListener('click', closeFilterDrawer);
      var filterHead = qs('.am-filter-head', panel);
      if (filterHead) filterHead.appendChild(closeBtn);
    }
  }

  /* ─── Submit Filters (AJAX) ─── */

  function submitFilters() {
    var form = qs('#AmFilterBody');
    if (!form) return;
    var url = new URL(window.location.href);
    var sortBy = url.searchParams.get('sort_by');
    var pageSize = url.searchParams.get('page_size') || url.searchParams.get('per_page');
    var viewMode = url.searchParams.get('view');

    Array.from(url.searchParams.keys()).forEach(function (k) {
      if (k.startsWith('filter.') || k === 'page' || k === 'q' || k === 'location') {
        url.searchParams.delete(k);
      }
    });
    clearLoadMoreParams(url);
    url.searchParams.delete('per_page');

    if (sortBy) url.searchParams.set('sort_by', sortBy);
    if (pageSize) url.searchParams.set('page_size', pageSize);
    if (viewMode) url.searchParams.set('view', viewMode);

    qsa('.am-filter-checkbox:checked', form).forEach(function (cb) {
      url.searchParams.append(cb.name, cb.value);
    });

    var aircraftSel = qs('#AmFilterAircraftType', form);
    if (aircraftSel && aircraftSel.value &&
        url.searchParams.getAll('filter.p.tag').indexOf(aircraftSel.value) === -1) {
      url.searchParams.append('filter.p.tag', aircraftSel.value);
    }

    var keywordInput = qs('#AmFilterKeyword', form);
    var keyword = keywordInput && keywordInput.value.trim() ? keywordInput.value.trim() : '';

    var locationInput = qs('#AmFilterLocation', form);

    if (keyword && url.pathname.indexOf('/search') !== 0) {
      url.pathname = '/search';
      url.searchParams.set('q', keyword);
      url.searchParams.set('type', 'product');
    } else if (keyword) {
      url.searchParams.set('q', keyword);
      url.searchParams.set('type', 'product');
    }

    if (locationInput && locationInput.value.trim()) {
      appendLocationFilter(url, locationInput.value);
    }

    var priceMin = qs('#FilterPrice-Min', form);
    var priceMax = qs('#FilterPrice-Max', form);
    var minVal = priceMin ? parsePriceInput(priceMin.value) : null;
    var maxVal = priceMax ? parsePriceInput(priceMax.value) : null;
    if (minVal != null && priceMin) url.searchParams.set(priceMin.name, String(minVal));
    if (maxVal != null && priceMax) url.searchParams.set(priceMax.name, String(maxVal));

    var marketUrl = otherMarketUrl(url, form);
    if (marketUrl) {
      window.location.assign(marketUrl);
      return;
    }

    fetchAndSwap(url.toString());
  }

  /* A market-* tag only narrows the current collection, so on a market's own
     page (e.g. /collections/uas-drones) ticking another market matched nothing.
     Send the visitor to that market's collection instead, keeping sort, view,
     location and price; the other facets belong to the old market. The page's
     own market is pre-ticked: unticking it goes to All Listings, and while it
     stays ticked its tag is dropped from the URL (the collection already is it). */
  function otherMarketUrl(url, form) {
    var tabs = qsa('.am-market-tab[data-market-tag]');
    var home = tabs.filter(function (tab) {
      return new URL(tab.href, window.location.origin).pathname === url.pathname;
    })[0];
    var homeTag = home ? home.getAttribute('data-market-tag') : '';
    if (!homeTag) return null; // All Listings / search: the tag filter works there

    var checked = qsa('.am-filter-group[data-filter-type="market"] .am-filter-checkbox:checked', form)
      .map(function (cb) { return cb.value; });
    var picked = checked.filter(function (v) { return v !== homeTag; })[0];
    var dest;
    if (picked) {
      dest = tabs.filter(function (tab) { return tab.getAttribute('data-market-tag') === picked; })[0];
    } else if (checked.indexOf(homeTag) === -1) {
      dest = tabs.filter(function (tab) { return !tab.getAttribute('data-market-tag'); })[0];
    } else {
      var kept = url.searchParams.getAll('filter.p.tag').filter(function (t) { return t !== homeTag; });
      url.searchParams.delete('filter.p.tag');
      kept.forEach(function (t) { url.searchParams.append('filter.p.tag', t); });
      return null;
    }
    if (!dest) return null;

    var target = new URL(dest.href, window.location.origin);
    ['sort_by', 'page_size', 'view', 'location', 'filter.v.price.gte', 'filter.v.price.lte'].forEach(function (k) {
      var v = url.searchParams.get(k);
      if (v) target.searchParams.set(k, v);
    });
    url.searchParams.getAll('filter.p.tag').forEach(function (t) {
      if (t.indexOf('location-') === 0) target.searchParams.append('filter.p.tag', t);
    });
    return target.toString();
  }

  /* ─── Apply Button ─── */

  function initFilterApply() {
    var applyBtn = qs('#AmFilterApply');
    if (!applyBtn || applyBtn.dataset.amBound === '1') return;
    applyBtn.dataset.amBound = '1';
    applyBtn.addEventListener('click', function () {
      var panel = qs('#AmFilterPanel');
      var overlay = qs('#AmFilterOverlay');
      if (panel && panel.classList.contains('am-filter-panel--open')) {
        panel.classList.remove('am-filter-panel--open');
        if (overlay) overlay.classList.remove('am-filter-overlay--visible');
        document.body.style.overflow = '';
      }
      submitFilters();
    });
  }

  /* ─── Auto-apply filters ───
     Ticking a filter or changing a dropdown applies it straight away (no Apply
     click needed). A short delay lets the visitor tick several boxes in a row
     and still send one request. Price and keyword fields apply when the visitor
     leaves the field or presses Enter, not on every keystroke. The Apply button
     stays for the mobile drawer, where it also closes the panel. */

  function initFilterAutoApply() {
    var form = qs('#AmFilterBody');
    if (!form || form.dataset.amAutoApply === '1') return;
    form.dataset.amAutoApply = '1';

    // The Aircraft type dropdown and the category checkboxes set the same
    // category-* tag; keep them in step so unticking one doesn't leave the
    // other re-adding the filter.
    var lastType = (qs('#AmFilterAircraftType', form) || {}).value || '';
    function typeBox(value) {
      return value ? qsa('.am-filter-checkbox', form).filter(function (cb) {
        return cb.value === value;
      })[0] : null;
    }
    function syncAircraftType(t) {
      var sel = qs('#AmFilterAircraftType', form);
      if (!sel) return;
      if (t === sel) {
        var prev = typeBox(lastType);
        if (prev) prev.checked = false;
        var next = typeBox(sel.value);
        if (next) next.checked = true;
      } else if (!t.checked && t.value === sel.value) {
        sel.value = '';
      }
      lastType = sel.value;
    }

    var timer = null;
    function schedule(delay) {
      clearTimeout(timer);
      autoApplyPending = true;
      timer = setTimeout(function () {
        autoApplyPending = false;
        submitFilters();
      }, delay);
    }

    form.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || !t.matches) return;
      if (t.matches('.am-filter-checkbox, select')) {
        syncAircraftType(t);
        schedule(350);
      } else if (t.matches('#FilterPrice-Min, #FilterPrice-Max, #AmFilterKeyword')) {
        schedule(0); // fires when the field loses focus
      }
    });

    form.addEventListener('keydown', function (e) {
      var t = e.target;
      if (e.key === 'Enter' && t && t.matches && t.matches('input:not([type=checkbox]):not([type=radio])')) {
        e.preventDefault();
        schedule(0);
      }
    });
  }

  /* ─── Filter Chips (AJAX removal) ─── */

  function initFilterChips() {
    document.addEventListener('click', function (e) {
      var locLink = e.target.closest('.am-chip-remove-location');
      if (locLink) {
        e.preventDefault();
        var url = new URL(window.location.href);
        var locParam = url.searchParams.get('location');
        url.searchParams.delete('location');
        url.searchParams.delete('page');
        clearLoadMoreParams(url);
        if (locParam) {
          var locTag = resolveLocationTag(locParam);
          if (locTag) {
            var kept = url.searchParams.getAll('filter.p.tag').filter(function (t) { return t !== locTag; });
            url.searchParams.delete('filter.p.tag');
            kept.forEach(function (t) { url.searchParams.append('filter.p.tag', t); });
          }
        }
        fetchAndSwap(url.toString());
        return;
      }

      var qLink = e.target.closest('.am-chip-remove-q');
      if (qLink) {
        e.preventDefault();
        var urlQ = new URL(window.location.href);
        urlQ.searchParams.delete('q');
        urlQ.searchParams.delete('page');
        clearLoadMoreParams(urlQ);
        fetchAndSwap(urlQ.toString());
        return;
      }

      var tagLink = e.target.closest('.am-chip-remove-tag');
      if (tagLink) {
        e.preventDefault();
        var tag = tagLink.dataset.tag;
        var urlTag = new URL(window.location.href);
        var kept = urlTag.searchParams.getAll('filter.p.tag').filter(function (t) { return t !== tag; });
        urlTag.searchParams.delete('filter.p.tag');
        kept.forEach(function (t) { urlTag.searchParams.append('filter.p.tag', t); });
        urlTag.searchParams.delete('page');
        clearLoadMoreParams(urlTag);
        fetchAndSwap(urlTag.toString());
      }

      var clearLink = e.target.closest('.am-chip--clear');
      if (clearLink) {
        e.preventDefault();
        fetchAndSwap(clearLink.href);
      }
    });
  }

  /* ─── Popstate (AJAX back/forward) ─── */

  window.addEventListener('popstate', function () {
    fetchAndSwap(window.location.href);
  });

  /* ─── Market Tabs ─── */

  function getMarketFromUrl() {
    var url = new URL(window.location.href);
    // Market is driven by the market-* product tag (matches the menu links,
    // the sidebar filter, and am-filter-engine). Check tags first.
    var tags = url.searchParams.getAll('filter.p.tag');
    var tagMap = {
      'market-aircraft': 'aircraft',
      'market-avionics': 'avionics',
      'market-engines': 'engines',
      'market-parts-products': 'parts',
      'market-real-estate': 'real-estate',
      'market-services': 'services',
      'market-uas-drones': 'uas-drones'
    };
    for (var i = 0; i < tags.length; i++) {
      if (tagMap[tags[i]]) return tagMap[tags[i]];
    }
    // Fallback: legacy product_type param
    var pType = url.searchParams.get('filter.p.product_type');
    if (pType) {
      var t = pType.trim();
      if (t === 'Aircraft') return 'aircraft';
      if (t === 'Avionics') return 'avionics';
      if (t === 'Engines') return 'engines';
      if (t === 'Parts & Products') return 'parts';
      if (t === 'Real Estate') return 'real-estate';
      if (t === 'Services') return 'services';
      if (t === 'UAS / Drones') return 'uas-drones';
    }
    return null;
  }

  function initMarketTabs() {
    var shell = qs('.am-market-tabs-shell');
    var tabs = qsa('.am-market-tab');
    if (!tabs.length) return;

    var currentHandle = getMarketFromUrl() || (shell ? (shell.dataset.currentHandle || 'all') : 'all');
    if (shell) shell.dataset.currentHandle = currentHandle;
    tabs.forEach(function (tab) {
      var tabValue = tab.dataset.tabValue || 'all';
      var isActive = tabValue === currentHandle;
      tab.classList.toggle('am-market-tab--active', isActive);
      tab.setAttribute('aria-selected', String(isActive));
    });
  }

  /* ─── Lead Modal ─── */

  function initLeadModal() {
    var modal = qs('#AmLeadModal');
    var closeBtn = qs('#AmLeadModalClose');
    var refEl = qs('#AmLeadModalListingRef');
    var listingTitleInput = qs('#AmLeadListingTitle');
    var listingHandleInput = qs('#AmLeadListingHandle');
    var ownerEmailInput = qs('#AmLeadOwnerEmail');
    var form = qs('#AmLeadForm');
    var successMsg = qs('.am-lead-success');
    if (!modal) return;

    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.am-lead-btn');
      if (!btn) return;
      e.preventDefault();
      if (listingTitleInput) listingTitleInput.value = btn.dataset.productTitle || '';
      if (listingHandleInput) listingHandleInput.value = btn.dataset.productHandle || '';
      if (ownerEmailInput) ownerEmailInput.value = btn.dataset.ownerEmail || '';
      if (refEl) refEl.textContent = 'Regarding: ' + (btn.dataset.productTitle || '');
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      var firstInput = modal.querySelector('input:not([type="hidden"])');
      if (firstInput) firstInput.focus();
    });

    function closeModal() {
      modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
      if (successMsg) successMsg.style.display = 'none';
      if (form) form.reset();
    }

    if (closeBtn) closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) closeModal();
    });

    if (form) {
      form.addEventListener('submit', function (e) {
        e.preventDefault();
        var data = new FormData(form);
        var submitBtn = form.querySelector('.am-lead-submit');
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending\u2026'; }
        fetch('/contact#contact_form', { method: 'POST', body: data })
          .then(function () {
            if (successMsg) successMsg.style.display = '';
            if (submitBtn) submitBtn.style.display = 'none';
            setTimeout(closeModal, 2800);
          })
          .catch(function () {
            if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Send Inquiry'; }
          });
      });
    }
  }

  /* ─── Init ─── */

  // Default sort order (AVIATOR #96): newest listings first — but ONLY as the
  // initial default on a clean first visit. Never override a sort the visitor
  // (or any other control) has set. We remember once the user has interacted so
  // a later param-less navigation doesn't snap back to date order.
  function applyDefaultSort() {
    var path = window.location.pathname;
    var isListingPage = path.indexOf('/collections/') === 0 || path.indexOf('/search') === 0;
    if (!isListingPage) return;

    var url = new URL(window.location.href);
    var isCollection = path.indexOf('/collections/') === 0;
    // Collections default to the stored "Newest activity" order (sort_by=manual),
    // which the seller portal keeps in latest-activity order. Search can't use a
    // stored order, so it keeps date order.
    var defaultSort = isCollection ? 'manual' : 'created-descending';

    // Collections already open in "Newest first" (their stored latest-activity
    // order) with a clean URL, so no sort param is ever added. Old default
    // links/bookmarks (sort_by=created-descending) and explicit sort_by=manual
    // are sent to the clean URL so visitors and search engines see one version.
    if (isCollection) {
      var s = url.searchParams.get('sort_by');
      if (s === 'created-descending' || s === 'manual') {
        url.searchParams.delete('sort_by');
        url.searchParams.delete('page');
        window.location.replace(url.toString());
      }
      return;
    }

    // The visitor already has a sort in the URL — respect it, nothing to do.
    if (url.searchParams.get('sort_by')) return;
    if (url.searchParams.get('am_partial')) return; // ajax load, ignore

    // Only apply the default on a genuinely fresh landing (no other filter/query
    // params present). If they're filtering/searching, leave their URL untouched.
    // NOTE: intentionally NOT gated by sessionStorage — every param-less landing
    // (including a fresh tab later in the same session) should show newest-first.
    var onlyBenign = true;
    url.searchParams.forEach(function (_v, k) {
      if (k !== 'view' && k !== 'page_size') onlyBenign = false;
    });
    if (!onlyBenign) return;

    url.searchParams.set('sort_by', defaultSort);
    window.location.replace(url.toString());
  }

  /* ─── AVIATOR-221: keep the Featured toggle WITHIN the current market ───
     Shopify doesn't reliably expose filter params to Liquid, so the server may
     build the toggle href without the market tag. Rebuild it from the real URL:
     preserve the current market-* tag and add/remove `featured`. */
  function initFeaturedToggle() {
    var toggle = qs('[data-am-featured-toggle]');
    if (!toggle) return;

    var url = new URL(window.location.href);
    var tags = url.searchParams.getAll('filter.p.tag');
    var marketTag = null;
    var featuredActive = false;
    tags.forEach(function (t) {
      if (t === 'featured') featuredActive = true;
      else if (t.indexOf('market-') === 0) marketTag = t;
    });

    var target = new URL(url.origin + url.pathname, url.origin); // e.g. /collections/all
    // Carry over sort/view/page-size so toggling doesn't reset them.
    ['sort_by', 'view', 'page_size', 'per_page'].forEach(function (k) {
      var v = url.searchParams.get(k);
      if (v) target.searchParams.set(k, v);
    });
    if (marketTag) target.searchParams.append('filter.p.tag', marketTag);
    if (!featuredActive) target.searchParams.append('filter.p.tag', 'featured');

    toggle.setAttribute('href', target.pathname + target.search);
    toggle.setAttribute('aria-pressed', featuredActive ? 'true' : 'false');
    if (featuredActive) toggle.classList.add('am-toolbar-featured-toggle--active');
    else toggle.classList.remove('am-toolbar-featured-toggle--active');

    /* AVIATOR-245 — correct the "(n)" for the market being viewed.
       Liquid can't see the filter params, so it renders the site-wide total and
       ships the per-market tags in data-am-featured-markets. Count the ones
       matching the active market and relabel (or hide the pill if none). */
    var actions = toggle.closest('.am-toolbar-actions');
    if (!actions) return;

    var total = parseInt(actions.getAttribute('data-am-featured-total'), 10);
    if (isNaN(total)) return;

    var count = total;
    if (marketTag) {
      var raw = actions.getAttribute('data-am-featured-markets') || '';
      count = raw.split(',').filter(function (t) { return t === marketTag; }).length;
    }

    if (count > 0) {
      toggle.textContent = '★ Featured (' + count + ')';
    } else if (featuredActive) {
      toggle.textContent = '★ Featured';
    } else {
      // Nothing featured in this market — the pill would lead to an empty page.
      toggle.style.display = 'none';
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (normalizeLocationUrl()) return;
    applyDefaultSort();
    updateKeywordBreadcrumb(window.location.href);
    initFeaturedToggle();
    initMarketTabs();
    initViewToggle();
    initToolbarControls();
    initLoadMore();
    initFilterApply();
    initFilterAutoApply();
    initFilterDrawer();
    initFilterChips();
    initLeadModal();
    applyMostViewedSort();
    if (typeof window.amApplyMarketFilters === 'function') {
      window.amApplyMarketFilters();
    }
    reconcilePageSizeFromUrl();
  });

})();
